import {
  FAMILY_HISTORY_STATUS_DEFAULT,
  deriveFamilyHistoryStatus,
  isFamilyHistoryStatus,
  memberHasHistory,
  type FamilyHistoryInput,
  type FamilyHistoryStatus,
} from '@helios/domain';

/**
 * PRD.267 — pure (DB/PowerSync-free) family-history logic: row shapes, grouping,
 * and the registration write-plan builder. Kept import-light so it is unit
 * testable under the repo's node:test harness.
 *
 * Family allergies and medications are family-history context only — the write
 * plan here NEVER targets patient_allergies or patient_medications.
 */

// ── Row types (PowerSync SQLite shapes) ──

export interface PatientFamilyHistoryRow {
  id: string;
  org_id: string | null;
  patient_id: string;
  history_status: string | null;
  notes: string | null;
  deleted_at: string | null;
}

export interface PatientFamilyMemberRow {
  id: string;
  org_id: string | null;
  patient_id: string;
  related_patient_id: string | null;
  relationship_type: string | null;
  relationship_label: string | null;
  name: string | null;
  sex: string | null;
  birth_date: string | null;
  age_text: string | null;
  notes: string | null;
  deleted_at: string | null;
}

export interface PatientFamilyMemberAllergyRow {
  id: string;
  org_id: string | null;
  patient_id: string;
  family_member_id: string;
  allergy_type: string | null;
  substance: string | null;
  reaction: string | null;
  severity: string | null;
  notes: string | null;
  deleted_at: string | null;
}

export interface PatientFamilyMemberMedicationRow {
  id: string;
  org_id: string | null;
  patient_id: string;
  family_member_id: string;
  name: string | null;
  dose: string | null;
  frequency: string | null;
  status: string | null;
  notes: string | null;
  deleted_at: string | null;
}

// ── Grouped view model ──

export interface FamilyMemberAllergyVM {
  id: string;
  allergyType?: string;
  substance: string;
  reaction?: string;
  severity?: string;
  notes?: string;
}

export interface FamilyMemberMedicationVM {
  id: string;
  name: string;
  dose?: string;
  frequency?: string;
  status?: string;
  notes?: string;
}

export interface FamilyMemberVM {
  id: string;
  relationshipType: string;
  relationshipLabel?: string;
  name?: string;
  sex?: string;
  birthDate?: string;
  ageText?: string;
  notes?: string;
  allergies: FamilyMemberAllergyVM[];
  medications: FamilyMemberMedicationVM[];
}

export interface FamilyHistoryVM {
  historyStatus: FamilyHistoryStatus;
  notes?: string;
  members: FamilyMemberVM[];
}

const undef = (v: string | null | undefined): string | undefined =>
  v == null || v === '' ? undefined : v;

/**
 * Pure assembly of the four family-history row sets into a grouped structure,
 * grouped by family member. Defensive against soft-deleted / orphaned children.
 */
export function groupFamilyHistory(
  history: PatientFamilyHistoryRow | null | undefined,
  members: PatientFamilyMemberRow[],
  allergies: PatientFamilyMemberAllergyRow[],
  medications: PatientFamilyMemberMedicationRow[],
): FamilyHistoryVM {
  const rawStatus = history?.history_status;
  const historyStatus: FamilyHistoryStatus = isFamilyHistoryStatus(rawStatus)
    ? rawStatus
    : FAMILY_HISTORY_STATUS_DEFAULT;

  const allergiesByMember = new Map<string, FamilyMemberAllergyVM[]>();
  for (const a of allergies) {
    if (a.deleted_at) continue;
    const list = allergiesByMember.get(a.family_member_id) ?? [];
    list.push({
      id: a.id,
      allergyType: undef(a.allergy_type),
      substance: a.substance ?? '',
      reaction: undef(a.reaction),
      severity: undef(a.severity),
      notes: undef(a.notes),
    });
    allergiesByMember.set(a.family_member_id, list);
  }

  const medsByMember = new Map<string, FamilyMemberMedicationVM[]>();
  for (const m of medications) {
    if (m.deleted_at) continue;
    const list = medsByMember.get(m.family_member_id) ?? [];
    list.push({
      id: m.id,
      name: m.name ?? '',
      dose: undef(m.dose),
      frequency: undef(m.frequency),
      status: undef(m.status),
      notes: undef(m.notes),
    });
    medsByMember.set(m.family_member_id, list);
  }

  const memberVMs: FamilyMemberVM[] = members
    .filter((m) => !m.deleted_at)
    .map((m) => ({
      id: m.id,
      relationshipType: m.relationship_type ?? '',
      relationshipLabel: undef(m.relationship_label),
      name: undef(m.name),
      sex: undef(m.sex),
      birthDate: undef(m.birth_date),
      ageText: undef(m.age_text),
      notes: undef(m.notes),
      allergies: allergiesByMember.get(m.id) ?? [],
      medications: medsByMember.get(m.id) ?? [],
    }));

  return {
    historyStatus,
    notes: undef(history?.notes),
    members: memberVMs,
  };
}

// ── Write plan (registration save) ──

/**
 * A single INSERT expressed as (table, column→value). Kept declarative so the
 * caller can run `db.execute` and so tests can assert that family records ONLY
 * ever target the four `patient_family_*` tables — never `patient_allergies`
 * or `patient_medications`.
 */
export interface FamilyWriteRow {
  table: string;
  cols: Record<string, string>;
}

/** Tables the family-history write path is allowed to touch. */
export const FAMILY_HISTORY_TABLES = [
  'patient_family_histories',
  'patient_family_members',
  'patient_family_member_allergies',
  'patient_family_member_medications',
] as const;

const put = (cols: Record<string, string>, key: string, val: string | undefined | null) => {
  if (val != null && val.trim() !== '') cols[key] = val.trim();
};

/**
 * Build the ordered list of INSERTs for a patient's family history.
 *
 * - Always emits exactly one `patient_family_histories` row (derived status).
 * - Emits a `patient_family_members` row only for members that have at least
 *   one allergy or medication (V1: empty member cards are not persisted).
 * - Family allergies/medications go to their dedicated child tables only.
 *
 * `org_id`, `patient_id`, `created_at`, and `updated_at` are always set so the
 * offline write includes every NOT NULL server column.
 */
export function buildFamilyHistoryInserts(
  input: FamilyHistoryInput,
  ctx: { patientId: string; orgId: string; genId: () => string; now?: string },
): FamilyWriteRow[] {
  const rows: FamilyWriteRow[] = [];
  const now = ctx.now ?? new Date().toISOString();
  const status = deriveFamilyHistoryStatus(input);

  const historyCols: Record<string, string> = {
    id: ctx.genId(),
    org_id: ctx.orgId,
    patient_id: ctx.patientId,
    history_status: status,
    created_at: now,
    updated_at: now,
  };
  put(historyCols, 'notes', input.notes);
  rows.push({ table: 'patient_family_histories', cols: historyCols });

  for (const member of input.members) {
    // V1: skip empty member cards (no allergy and no medication).
    if (!memberHasHistory(member)) continue;

    const memberId = member.id ?? ctx.genId();
    const memberCols: Record<string, string> = {
      id: memberId,
      org_id: ctx.orgId,
      patient_id: ctx.patientId,
      relationship_type: member.relationshipType,
      created_at: now,
      updated_at: now,
    };
    put(memberCols, 'relationship_label', member.relationshipLabel);
    put(memberCols, 'name', member.name);
    put(memberCols, 'sex', member.sex);
    put(memberCols, 'birth_date', member.birthDate);
    put(memberCols, 'age_text', member.ageText);
    put(memberCols, 'notes', member.notes);
    rows.push({ table: 'patient_family_members', cols: memberCols });

    for (const allergy of member.allergies) {
      if (!allergy.substance || !allergy.substance.trim()) continue;
      const aCols: Record<string, string> = {
        id: allergy.id ?? ctx.genId(),
        org_id: ctx.orgId,
        patient_id: ctx.patientId,
        family_member_id: memberId,
        substance: allergy.substance.trim(),
        created_at: now,
        updated_at: now,
      };
      put(aCols, 'allergy_type', allergy.allergyType);
      put(aCols, 'reaction', allergy.reaction);
      put(aCols, 'severity', allergy.severity);
      put(aCols, 'notes', allergy.notes);
      rows.push({ table: 'patient_family_member_allergies', cols: aCols });
    }

    for (const med of member.medications) {
      if (!med.name || !med.name.trim()) continue;
      const mCols: Record<string, string> = {
        id: med.id ?? ctx.genId(),
        org_id: ctx.orgId,
        patient_id: ctx.patientId,
        family_member_id: memberId,
        name: med.name.trim(),
        created_at: now,
        updated_at: now,
      };
      put(mCols, 'dose', med.dose);
      put(mCols, 'frequency', med.frequency);
      put(mCols, 'status', med.status);
      put(mCols, 'notes', med.notes);
      rows.push({ table: 'patient_family_member_medications', cols: mCols });
    }
  }

  return rows;
}
